import { useEffect, useState } from "react";
import { Badge, Button, Card, Input, message, Segmented, Space, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { RequestStatus, traineesActions } from "@fitness-app/app-store";
import { DEFAULT_TRAINEE_LIST_FILTERS } from "@fitness-app/app-store/src/store/reducers/trainees/reducer";
import { type ExerciseRecordingWithExerciseInfo } from "@fitness-app/data-models/entities/ExerciseRecording";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useExerciseRecordings } from "~/modules/Trainee/hooks/useExerciseRecordings";
import { useTrainersFilters } from "~/modules/Trainee/hooks/useTrainersFilters";
import { ScheduledMessagesTable } from "~/modules/Trainee/ScheduledMessagesTable";
import { TraineeVideoRecordingsTable } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/TraineeVideoRecordingsTable";
import { useUpdateExerciseRecording } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useUpdateExerciseRecording";
import ClientRatingTable from "~/modules/Trainee/ClientRatingTable";
import TraineesActivityTable from "~/modules/Trainee/TraineesActivityTable";
import TraineesTable from "~/modules/Trainee/TrainnesTable";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { useScheduledMessagesSequenceCount } from "./hooks/useScheduledMessagesSequence";

const TraineeList = () => {
  const { t } = useTranslation("trainees");
  const [activeTab, setActiveTab] = useState("active");
  const [activityListType, setActivityListType] = useState<"activity" | "rating">("activity");
  const { searchStatus, searchTerm, filters, list } = useAppSelector((store) => store.trainees);
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useAppDispatch();
  const { data, count, isLoading, statusFilter, changeStatusFilter } = useExerciseRecordings();
  const { data: scheduledMessagesSequenceCount } = useScheduledMessagesSequenceCount();
  const { deleteExerciseRecordingVideo, archiveExerciseRecording } = useUpdateExerciseRecording();
  const [searchParams, setSearchParams] = useSearchParams();
  // TRE-1636: sales reps only get the plain client table — no activity stats,
  // recordings or group messages.
  const { isSalesRep } = useUserRole();
  const rawTabFromUrl = searchParams.get("tab") || "active";
  // Sales reps only render the "active" pane; any other tab (activities,
  // recordings, scheduled-messages) would show a blank area, so normalize it.
  const tabFromUrl = isSalesRep && rawTabFromUrl !== "active" ? "active" : rawTabFromUrl;

  useEffect(() => {
    if (isSalesRep && rawTabFromUrl !== "active") {
      setSearchParams({ tab: "active" });
    }
  }, [isSalesRep, rawTabFromUrl, setSearchParams]);

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const { trainers, showOnlyMyTrainers, toggleMyTrainerFilters, canToggleAssignedTrainers } =
    useTrainersFilters(activeTab);

  const onClear = () => {
    setSearchValue("");
    dispatch(traineesActions.setSearchTerm(""));
    void dispatch(traineesActions.fetchTrainees(DEFAULT_TRAINEE_LIST_FILTERS));
  };

  const isSearchMode = Boolean(searchTerm);

  const handleDeleteVideo = async (recording: ExerciseRecordingWithExerciseInfo) => {
    const path = recording.metadata?.path;
    await deleteExerciseRecordingVideo(recording);
    if (path) {
      const { error } = await supabase.storage.from("users").remove([path]);
      if (error) {
        message.error(`Błąd podczas usuwania filmu: ${error.message}`);
      }
    }
  };

  const handleArchiveRecording = async (recording: ExerciseRecordingWithExerciseInfo) => {
    await archiveExerciseRecording(recording);
    message.success("Nagranie zostało zarchiwizowane.");
  };

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (isSearchMode) {
        onClear();
      }
      return;
    }
    dispatch(traineesActions.setSearchTerm(trimmed));

    // Kto szuka klientki po nazwisku, chce ją znaleźć niezależnie od tego, jaki
    // status i typ dostępu były akurat zaznaczone w kolumnach — filtry zdejmujemy
    // (kolumny pokazują „wszystkie"), a po wyczyszczeniu wracają domyślne.
    void dispatch(traineesActions.fetchTrainees({ statuses: null, accessType: null }));
  };

  return (
    <Card title={t("title")}>
      <Space size={4}>
        <Input.Search
          placeholder={t<string>("search.placeholder")}
          onSearch={onSearch}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ width: 300, margin: "20px 0" }}
          loading={searchStatus === RequestStatus.FETCHING}
        />
        {Boolean(searchTerm) && (
          <Button onClick={onClear} type="link">
            {t("products:search.reset")}
          </Button>
        )}
      </Space>
      <Tabs
        destroyInactiveTabPane
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setSearchParams({ tab: key });
        }}
        tabBarExtraContent={
          activeTab === "activities" ? (
            <Segmented
              value={activityListType}
              options={[
                { value: "activity", label: "Aktywność" },
                { value: "rating", label: "Rating" },
              ]}
              onChange={(value) => setActivityListType(value as "activity" | "rating")}
            />
          ) : canToggleAssignedTrainers ? (
            <Segmented
              className={activeTab !== "active" ? "hidden" : ""}
              value={showOnlyMyTrainers ? "filtered" : "all"}
              options={[
                { value: "filtered", label: "Tylko przypisani trenerzy" },
                { value: "all", label: "Wszyscy trenerzy" },
              ]}
              onChange={(value) => {
                toggleMyTrainerFilters(value === "filtered" ? true : false);
              }}
            />
          ) : null
        }
      >
        <Tabs.TabPane
          tab={`${
            filters.currentStatusFilter?.length === 4 || filters.currentStatusFilter?.length === 0
              ? t("products:clients.all")
              : t(`products:clients.${filters.currentStatusFilter?.[0]?.toLowerCase() || "all"}`)
          }${searchTerm ? ` (${list.length})` : ""}`}
          key="active"
        >
          <TraineesTable trainers={trainers} isSearchMode={isSearchMode} />
        </Tabs.TabPane>
        {!isSalesRep && (
          <Tabs.TabPane key="activities" tab="Statystyki aktywności">
            {activityListType === "activity" ? (
              <TraineesActivityTable isSearchMode={isSearchMode} />
            ) : (
              <ClientRatingTable isSearchMode={isSearchMode} />
            )}
          </Tabs.TabPane>
        )}
        {!isSalesRep && (
          <Tabs.TabPane
            key="recordings"
            tab={
              <div className="space-x-2">
                Nagrania do oceny <Badge count={count} />
              </div>
            }
          >
            <TraineeVideoRecordingsTable
              statusFilter={statusFilter}
              changeStatusFilter={changeStatusFilter}
              withTraineeInfo
              data={data || []}
              isLoading={isLoading}
              onDeleteVideo={handleDeleteVideo}
              onArchiveRecording={handleArchiveRecording}
            />
          </Tabs.TabPane>
        )}
        {!isSalesRep && (
          <Tabs.TabPane
            key="scheduled-messages"
            tab={
              <div className="space-x-2">
                Wiadomości grupowe <Badge color="geekblue" count={scheduledMessagesSequenceCount} />
              </div>
            }
          >
            <ScheduledMessagesTable trainers={trainers} />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Card>
  );
};

export default TraineeList;
